import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import {
  formatPlainTextToMarkdown,
  isMarkdownFormatted,
} from "../../utils/textFormatter";
import styles from "./markdown.module.css";
import clsx from "clsx";

interface MarkdownRendererProps {
  content: string;
  autoFormat?: boolean; // Auto-format plain text to markdown
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  autoFormat = true,
  className,
}) => {
  // Auto-format plain text if enabled and content is not already markdown
  const formattedContent =
    autoFormat && !isMarkdownFormatted(content)
      ? formatPlainTextToMarkdown(content)
      : content;

  return (
    <div className={clsx(styles.markdown, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={
          {
            // Customize heading styles
            h1: ({ node, ...props }) => <h1 className={styles.h1} {...props} />,
            h2: ({ node, ...props }) => <h2 className={styles.h2} {...props} />,
            h3: ({ node, ...props }) => <h3 className={styles.h3} {...props} />,
            h4: ({ node, ...props }) => <h4 className={styles.h4} {...props} />,
            h5: ({ node, ...props }) => <h5 className={styles.h5} {...props} />,
            h6: ({ node, ...props }) => <h6 className={styles.h6} {...props} />,

            // Customize list styles
            ul: ({ node, ...props }) => <ul className={styles.ul} {...props} />,
            ol: ({ node, ...props }) => <ol className={styles.ol} {...props} />,
            li: ({ node, ...props }) => <li className={styles.li} {...props} />,

            // Customize paragraph
            p: ({ node, ...props }) => <p className={styles.p} {...props} />,

            // Customize code blocks
            code: ({ node, className, children, ...props }) => {
              const match = /language-(\w+)/.exec(className || "");
              const isInline = !match && !className;
              return isInline ? (
                <code className={styles.inlineCode} {...props}>
                  {children}
                </code>
              ) : (
                <code className={styles.codeBlock} {...props}>
                  {children}
                </code>
              );
            },
            pre: ({ node, ...props }) => (
              <pre className={styles.pre} {...props} />
            ),

            // Customize blockquote
            blockquote: ({ node, ...props }) => (
              <blockquote className={styles.blockquote} {...props} />
            ),

            // Customize table
            table: ({ node, ...props }) => (
              <table className={styles.table} {...props} />
            ),
            thead: ({ node, ...props }) => (
              <thead className={styles.thead} {...props} />
            ),
            tbody: ({ node, ...props }) => (
              <tbody className={styles.tbody} {...props} />
            ),
            tr: ({ node, ...props }) => <tr className={styles.tr} {...props} />,
            th: ({ node, ...props }) => <th className={styles.th} {...props} />,
            td: ({ node, ...props }) => <td className={styles.td} {...props} />,

            // Customize links
            a: ({ node, ...props }) => (
              <a
                className={styles.link}
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              />
            ),

            // Customize strong/bold
            strong: ({ node, ...props }) => (
              <strong className={styles.strong} {...props} />
            ),

            // Customize emphasis/italic
            em: ({ node, ...props }) => <em className={styles.em} {...props} />,
          } as Components
        }
      >
        {formattedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
